import { registerView } from '../viewRegistry';
import { interactiveRegistry, navigateTo } from '../interactivity';
import { getCanonicalSafeArea } from '../safeArea';
import { renderBlobBackground } from '../renderer/plasma';
import { getCurrentUserSync } from '../auth/authService';
import { DopeTextRenderer } from '../renderer/bitmapFont';
import { getChatMessages } from './chat';
import { COPY } from '../../content/copy';
import useGameState from '../gameState';
import DopeUI, { TERMINAL } from '../ui/DopeOS-UI';
import { isMobileDevice } from '../device';
import {
  APP_CATALOG,
  getInstalledApps,
  getActiveNotifications,
  dismissNotification,
  isNewApp,
  markAppSeen,
} from '../installedApps';

/**
 * DopeOS desktop (ADR-0020): installed apps only. The big DCW logo lives at
 * boot; the shell carries a small DOPEOS mark in the status line.
 */

let keyboardCache = null;
function hasKeyboard() {
  if (keyboardCache !== null) return keyboardCache;
  let fine = true;
  try {
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      fine = window.matchMedia('(pointer: fine)').matches;
    }
  } catch {
    /* default to keyboard present */
  }
  keyboardCache = fine && !isMobileDevice();
  return keyboardCache;
}

function drawChip(ctx, x, y, label, value) {
  const width = (label.length + String(value).length + 4) * 8;
  ctx.fillStyle = TERMINAL.GREEN_DARKEST;
  ctx.fillRect(x, y, width, 18);
  ctx.strokeStyle = TERMINAL.GREEN_DARK;
  ctx.lineWidth = 1;
  ctx.strokeRect(x, y, width, 18);
  ctx.fillStyle = TERMINAL.TEXT_DIM;
  DopeTextRenderer.renderDOSText(ctx, label, x + 4, y + 1, 1);
  ctx.fillStyle = TERMINAL.TEXT;
  DopeTextRenderer.renderDOSText(ctx, String(value), x + (label.length + 2) * 8, y + 1, 1);
  return width;
}

// Small DOPEOS identity mark; dim when the line is offline.
function drawMark(ctx, x, y, online) {
  ctx.fillStyle = online ? TERMINAL.GREEN : TERMINAL.TEXT_DIM;
  DopeTextRenderer.renderDOSText(ctx, 'DOPEOS', x, y, 1);
}

function drawStatus(ctx, width, mobile = false) {
  const state = useGameState.getState();
  const core = state.core || {};
  const online = core.status === 'online';
  const cash = `$${Number(online ? core.cash : state.playerCash || 0).toFixed(2)}`;
  const debt = `$${Number(online ? core.debt : 0).toFixed(2)}`;
  const heat = `${online ? core.heat ?? 0 : 0}`;
  const day = core.day == null ? '--' : `${core.day}/30`;
  const borough = (core.districtId || state.currentBorough || '--').toString().toUpperCase().slice(0, 10);
  const chips = [
    ['CASH', cash],
    ['DEBT', debt],
    ['HEAT', heat],
    ['DAY', day],
  ];
  if (mobile) chips.push(['BOROUGH', borough]);

  if (mobile) {
    let x = 8;
    for (const [label, value] of chips.slice(0, 3)) {
      x += drawChip(ctx, x, 3, label, value) + 8;
    }
    x = 8;
    for (const [label, value] of chips.slice(3)) {
      x += drawChip(ctx, x, 24, label, value) + 8;
    }
    drawMark(ctx, width - 56, 27, online);
    return;
  }

  let x = 8;
  for (const [label, value] of chips) {
    x += drawChip(ctx, x, 5, label, value) + 8;
  }
  drawMark(ctx, width - 56, 5, online);
}

function appElementId(app) {
  return app.id === 'apps' ? 'home-more' : `home-${app.id}`;
}

function openApp(app) {
  markAppSeen(app.id);
  navigateTo(app.view);
}

function drawIcons(ctx, x, y, w, apps, cols, cellH) {
  const cellW = Math.floor(w / cols);
  const boxSize = 48;
  apps.forEach((app, index) => {
    const col = index % cols;
    const row = Math.floor(index / cols);
    const cx = x + col * cellW;
    const cy = y + row * cellH;
    const bx = cx + Math.floor((cellW - boxSize) / 2);
    ctx.fillStyle = TERMINAL.GREEN_DARKEST;
    ctx.fillRect(bx, cy, boxSize, boxSize);
    ctx.strokeStyle = TERMINAL.GREEN;
    ctx.lineWidth = 1;
    ctx.strokeRect(bx + 0.5, cy + 0.5, boxSize - 1, boxSize - 1);
    ctx.fillStyle = TERMINAL.TEXT;
    const glyphW = app.glyph.length * 16;
    DopeTextRenderer.renderDOSText(ctx, app.glyph, bx + Math.floor((boxSize - glyphW) / 2), cy + 8, 2);
    ctx.fillStyle = TERMINAL.TEXT;
    const labelW = app.label.length * 8;
    DopeTextRenderer.renderDOSText(ctx, app.label, cx + Math.floor((cellW - labelW) / 2), cy + boxSize + 6, 1);
    if (isNewApp(app.id)) {
      ctx.fillStyle = TERMINAL.TEXT_BRIGHT;
      DopeTextRenderer.renderDOSText(ctx, 'NEW', bx + boxSize - 24, cy - 14, 1);
    }
    interactiveRegistry.register({
      id: appElementId(app),
      x: cx + 2,
      y: cy - 2,
      w: cellW - 4,
      h: cellH - 8,
      skipInflation: true,
      onClick: () => openApp(app),
    });
  });
}

function clipWords(value, max) {
  if (value.length <= max) return value;
  const cut = value.slice(0, Math.max(1, max - 3));
  const space = cut.lastIndexOf(' ');
  const head = space > 0 ? cut.slice(0, space) : cut;
  return `${head}...`;
}

function drawSystemLog(ctx, x, y, w, h, user) {
  DopeUI.Panel(ctx, x, y, w, h, 'SYSTEM');
  const core = useGameState.getState().core || {};
  const identity = user?.username
    ? `@${user.username}`
    : user?.address
      ? `${user.address.slice(0, 6)}...${user.address.slice(-4)}`
      : core.playerId
        ? `GUEST ${core.playerId.slice(-6)}`
        : 'GUEST SESSION';
  const online = core.status === 'online';
  const lines = [
    [`OPERATOR ${identity}`, TERMINAL.TEXT],
    [online ? COPY.home.online : COPY.home.offline, online ? TERMINAL.GREEN : TERMINAL.TEXT_DIM],
  ];
  const last = getChatMessages().slice(-1)[0];
  if (last) {
    const max = Math.max(8, Math.floor((w - 24) / 8) - 3);
    const prefix = last.role === 'user' ? '>' : 'V:';
    lines.push([clipWords(`${prefix} ${last.text}`, max), TERMINAL.TEXT_DIM]);
  } else {
    lines.push([COPY.signals.empty, TERMINAL.TEXT_DIM]);
  }
  lines.forEach(([line, color], i) => {
    if (y + 30 + i * 18 + 16 > y + h) return;
    ctx.fillStyle = color;
    DopeTextRenderer.renderDOSText(ctx, line, x + 12, y + 30 + i * 18, 1);
  });
}

function drawNotification(ctx, time, x, y, w, h) {
  const [note] = getActiveNotifications().slice(-1);
  if (!note) return;
  const pulse = Math.floor(time * 2) % 2 === 0;
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(x, y, w, h);
  DopeUI.Panel(ctx, x, y, w, h, 'NEW APP');
  ctx.fillStyle = pulse ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT;
  const max = Math.floor((w - 24) / 8);
  DopeTextRenderer.renderDOSText(ctx, note.text.slice(0, max), x + 12, y + 30, 1);
  interactiveRegistry.register({
    id: 'home-notification',
    x, y, w, h,
    skipInflation: true,
    onClick: () => {
      const app = APP_CATALOG.find((a) => a.id === note.appId);
      dismissNotification(note.id);
      if (app) openApp(app);
    },
  });
}

registerView('dopeos_home', function renderHome(ctx, time, width, height, params = {}) {
  interactiveRegistry.clear();
  const safe = getCanonicalSafeArea({ w: width || 640, h: height || 400 });
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, 0, safe.width, safe.height);
  ctx.save();
  ctx.globalAlpha = 0.16;
  renderBlobBackground(ctx, time, safe.width, safe.height);
  ctx.restore();

  const mobile = safe.width < 500;
  const statusH = mobile ? 48 : 24;
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, 0, safe.width, statusH);
  drawStatus(ctx, safe.width, mobile);
  ctx.strokeStyle = TERMINAL.GREEN_DARK;
  ctx.beginPath();
  ctx.moveTo(0, statusH);
  ctx.lineTo(safe.width, statusH);
  ctx.stroke();

  const state = useGameState.getState();
  const core = state.core || {};
  if (!mobile) {
    const borough = (core.districtId || state.currentBorough || 'BRONX').toString().toUpperCase().slice(0, 12);
    ctx.fillStyle = TERMINAL.TEXT_DIM;
    DopeTextRenderer.renderDOSText(ctx, `${borough} // DESKTOP`, 8, 34, 1);
  }

  const apps = getInstalledApps()
    .map((id) => APP_CATALOG.find((a) => a.id === id))
    .filter(Boolean);
  const user = getCurrentUserSync();

  const cols = mobile ? 3 : 6;
  const cellH = mobile ? 92 : 84;
  const iconsY = statusH + (mobile ? 24 : 40);
  drawIcons(ctx, 8, iconsY, safe.width - 16, apps, cols, cellH);
  const rows = Math.ceil(apps.length / cols);

  // System tray: secondary system actions (not apps).
  const trayH = mobile ? 44 : 22;
  const trayY = safe.height - trayH - (hasKeyboard() ? 34 : 8);
  const tray = [
    { id: 'leaderboard', label: 'RANK', run: params.onLeaderboardClick },
    { id: 'about', label: 'ABOUT', run: params.onAboutClick },
    { id: 'disconnect', label: 'DISCONNECT', run: params.onDisconnectClick },
  ];
  const gap = 8;
  const tw = Math.floor((safe.width - 16 - gap * (tray.length - 1)) / tray.length);
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, trayY - 2, safe.width, trayH + 4);
  tray.forEach((item, i) => {
    const bx = 8 + i * (tw + gap);
    DopeUI.Button(ctx, bx, trayY, tw, trayH, item.label, 'normal');
    interactiveRegistry.register({
      id: `home-${item.id}`, x: bx, y: trayY, w: tw, h: trayH, skipInflation: true,
      onClick: () => item.run && item.run(),
    });
  });

  const logY = iconsY + rows * cellH + 4;
  const logH = trayY - 8 - logY;
  if (logH >= 64) drawSystemLog(ctx, 8, logY, safe.width - 16, Math.min(logH, mobile ? 120 : 96), user);

  if (hasKeyboard()) {
    ctx.fillStyle = TERMINAL.BLACK;
    ctx.fillRect(0, safe.height - 24, safe.width, 20);
    ctx.fillStyle = TERMINAL.TEXT_DIM;
    DopeTextRenderer.renderDOSText(ctx, 'TAB FOCUS   ENTER OPEN', 8, safe.height - 20, 1);
  }

  // Anchored just above the tray so it never covers the icon row.
  const toastH = mobile ? 56 : 52;
  drawNotification(ctx, time, 8, trayY - toastH - 6, Math.min(safe.width - 16, 400), toastH);
});

export default 'dopeos_home';
